import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Layers, Sparkles, Palette, Download } from "lucide-react";
import { HeaderNav } from "../components/HeaderNav";
import { UploadDrop } from "../components/studio/UploadDrop";
import { PreviewStage } from "../components/studio/PreviewStage";
import { VersionStrip } from "../components/studio/VersionStrip";
import { JobProgress } from "../components/studio/JobProgress";
import { BackgroundTab } from "../components/studio/BackgroundTab";
import { EffectsTab } from "../components/studio/EffectsTab";
import { ColorsTab } from "../components/studio/ColorsTab";
import { ExportTab } from "../components/studio/ExportTab";
import { api, ACTIVE, errDetail, fileUrl } from "../lib/api";
import { NEUTRAL, buildChain, chainToCss } from "../lib/filters";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const TABS = [
  { id: "bg", icon: Layers, key: "tab_bg" },
  { id: "fx", icon: Sparkles, key: "tab_fx" },
  { id: "colors", icon: Palette, key: "tab_colors" },
  { id: "export", icon: Download, key: "tab_export" },
];

const triggerDownload = (file) => {
  const a = document.createElement("a");
  a.href = fileUrl(file, true);
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const useProject = (id) => {
  const { t, te } = useLang();
  const { refresh: refreshUser } = useAuth();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const prev = useRef({});
  const load = useCallback(async () => {
    try {
      const p = await api.project(id);
      p.jobs.forEach((j) => {
        const before = prev.current[j.id];
        if (before && ACTIVE.includes(before) && j.status === "done") {
          toast.success(t("job_done"));
          if (j.type === "export" && j.result) triggerDownload(j.result);
        }
        if (before && ACTIVE.includes(before) && j.status === "error") {
          toast.error(`${t("job_failed")} : ${te(j.error)}${j.refunded ? ` · ${t("refunded_note")}` : ""}`);
          refreshUser();
        }
        prev.current[j.id] = j.status;
      });
      setProject(p);
      setError("");
    } catch (e) {
      setError(te(errDetail(e)));
    }
  }, [id, t, te, refreshUser]);
  useEffect(() => { prev.current = {}; setProject(null); if (id) load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const active = project?.jobs.some((j) => ACTIVE.includes(j.status));
  useEffect(() => {
    if (!active) return undefined;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [active, load]);
  return { project, setProject, load, error };
};

const EmptyStudio = () => {
  const { t } = useLang();
  const nav = useNavigate();
  const onFile = async (f) => {
    try {
      const p = await api.createProject(f.id);
      nav(`/studio/${p.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="frz-empty" data-testid="studio-empty">
      <UploadDrop testId="source-upload" title={t("upload_title")} hint={t("upload_hint")} onFile={onFile} />
    </div>
  );
};

export default function Studio() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, te } = useLang();
  const { project, setProject, load, error } = useProject(id);
  const [tab, setTab] = useState("bg");
  const [colorState, setColorState] = useState(NEUTRAL);
  const [format, setFormat] = useState("original");
  const [noCredits, setNoCredits] = useState(false);
  const { refresh: refreshUser } = useAuth();
  const chain = useMemo(() => buildChain(colorState), [colorState]);

  useEffect(() => { setColorState(NEUTRAL); setFormat("original"); setNoCredits(false); }, [id]);

  if (!id) return (<div className="frz-page"><HeaderNav /><EmptyStudio /></div>);
  if (error) return (<div className="frz-page"><HeaderNav /><p className="frz-error frz-center" data-testid="studio-error">{error}</p></div>);
  if (!project) return (<div className="frz-page"><HeaderNav /><div className="frz-center frz-muted">…</div></div>);

  const current = project.versions[project.current]?.file;
  const jobs = project.jobs;
  const aiActive = jobs.some((j) => ACTIVE.includes(j.status) && j.type !== "export");
  const exportActive = jobs.some((j) => ACTIVE.includes(j.status) && j.type === "export");
  const shownJob = jobs[0];

  const launch = async (payload) => {
    setNoCredits(false);
    try {
      await api.createJob({ project_id: project.id, ...payload });
      toast.success(t("job_launched"));
      load();
      refreshUser();
    } catch (e) {
      if (e.response?.status === 402) setNoCredits(true);
      else toast.error(te(errDetail(e)));
    }
  };
  const select = async (i) => setProject(await api.setCurrent(project.id, i));
  const undo = async () => setProject(await api.undo(project.id));

  return (
    <div className="frz-page frz-studio-page">
      <HeaderNav />
      <main className="frz-studio" data-testid="studio">
        <section className="frz-stage">
          <PreviewStage file={current} filter={chainToCss(chain)} format={format} />
          {shownJob && (ACTIVE.includes(shownJob.status) || shownJob.status === "error") && (
            <JobProgress job={shownJob} isVideo={project.kind === "video" && shownJob.type !== "export"} />
          )}
          <VersionStrip project={project} onSelect={select} onUndo={undo} busy={aiActive} />
        </section>
        <aside className="frz-panel" data-testid="tool-panel">
          <div className="frz-tabs" role="tablist">
            {TABS.map(({ id: tid, icon: Icon, key }) => (
              <button key={tid} type="button" role="tab" aria-selected={tab === tid} className={tab === tid ? "on" : ""} onClick={() => setTab(tid)} data-testid={`tab-${tid}`}>
                <Icon size={16} /> <span>{t(key)}</span>
              </button>
            ))}
          </div>
          {noCredits && (
            <div className="frz-nocredits" data-testid="insufficient-credits-banner">
              <span>{t("no_credits")}</span>
              <Link to="/recharge" className="frz-btn frz-btn-primary frz-btn-sm" data-testid="recharge-button">{t("recharge")}</Link>
            </div>
          )}
          {tab === "bg" && <BackgroundTab kind={project.kind} onLaunch={launch} disabled={aiActive} costs={project.costs} />}
          {tab === "fx" && current && <EffectsTab file={current} onLaunch={launch} disabled={aiActive} costs={project.costs} />}
          {tab === "colors" && current && <ColorsTab file={current} state={colorState} setState={setColorState} />}
          {tab === "export" && (
            <ExportTab project={project} format={format} setFormat={setFormat} disabled={exportActive || aiActive}
              onExport={() => launch({ type: "export", colors: chain, format, label: t(`fmt_${format}`) })} />
          )}
          <button type="button" className="frz-btn frz-btn-ghost frz-btn-sm frz-new" onClick={() => nav("/studio")} data-testid="new-media-button">
            <ImagePlus size={15} /> {t("new_media")}
          </button>
        </aside>
      </main>
    </div>
  );
}
